import { cn } from '@/lib/utils'
import React from 'react'
import { Label } from './label'

interface FormFieldProps {
  id: string
  label?: string
  error?: boolean
  required?: boolean
  children: React.ReactNode
  className?: string
  showError?: boolean // New prop to control error message display
}

// Type for React component with name and displayName
interface NamedComponent {
  name?: string
  displayName?: string
  $$typeof?: symbol
}

export const FormField: React.FC<FormFieldProps> = ({
  id,
  label,
  error = false,
  required = false,
  children,
  className,
  showError = true, // Default to true for backward compatibility
}) => {
  const child = React.Children.only(children) as React.ReactElement

  // Get existing props and className
  const existingProps = child.props || {}
  const existingClassName = existingProps.className || ''

  // Check if child is an Input component with proper type guards
  const childType = child.type as NamedComponent
  const isInputComponent =
    (typeof child.type === 'function' &&
      (childType.name === 'Input' || childType.displayName === 'Input')) ||
    // Check if it's a forwardRef component with Input display name
    (typeof child.type === 'function' &&
      childType.$$typeof === Symbol.for('react.forward_ref') &&
      childType.displayName === 'Input')

  // Create new props with error state
  const newProps = {
    ...existingProps,
    id,
    'aria-invalid': error ? 'true' : 'false',
    'aria-describedby': error && showError ? `${id}-error` : undefined,
    // Only pass error prop to Input components
    ...(isInputComponent && { error }),
    className: cn(existingClassName, error && '!border-red-500 !focus-visible:ring-red-500'),
  }

  return (
    <div className={cn('space-y-2', className)}>
      {label && (
        <Label htmlFor={id} className={cn(error && 'text-red-600')}>
          {label}
          {required && <span className="text-red-600 ml-1">*</span>}
        </Label>
      )}
      {React.cloneElement(child, newProps)}
      {error && showError && (
        <p id={`${id}-error`} className="text-sm text-red-600">
          This field is required
        </p>
      )}
    </div>
  )
}
